import type { FunctionComponent, ComponentChildren } from 'preact'
import { toChildArray } from 'preact'
import { html } from 'htm/preact'
import { APP_SIGNUP_URL } from '../constants.js'
import './tool-page.css'

interface ToolPageProps {
    title:string;
    eyebrow?:string;
    description?:string;
    children?:ComponentChildren;
}

export const ToolPage:FunctionComponent<ToolPageProps> = function ({
    title,
    eyebrow,
    description,
    children,
}) {
    return html`<div class="route tool-page">
        <a class="tool-page-back" href="/tools">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                class="tool-page-back-arrow"
            >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M5 12l14 0" />
                <path d="M5 12l4 4" />
                <path d="M5 12l4 -4" />
            </svg>
            All tools
        </a>
        <div class="tool-page-head">
            ${eyebrow ?
                html`<p class="tool-page-eyebrow">${eyebrow}</p>` :
                null
            }
            <h2 class="tool-page-title">${title}</h2>
            ${description ?
                html`<p class="tool-page-description">${description}</p>` :
                null
            }
        </div>
        <div class="tool-page-body">
            ${children}
        </div>
        <aside class="tool-page-cta">
            <div class="tool-page-cta-copy">
                <p class="tool-page-cta-eyebrow">Stay audit-ready</p>
                <p class="tool-page-cta-text">
                    Log refrigerant transactions once in the app and keep a
                    permanent, EPA-compliant record all year.
                </p>
            </div>
            <a href=${APP_SIGNUP_URL} class="tool-page-cta-action">
                Start free
            </a>
        </aside>
    </div>`
}

interface ToolWorkbenchProps {
    /* Explicit result content. When omitted, the second child is used as
       the result and the first child as the form — so a tool can simply
       wrap its <form> and its result block in <ToolWorkbench>. A falsy
       result shows the empty state. */
    result?:ComponentChildren;
    inputsLabel?:string;
    resultsLabel?:string;
    emptyHint?:string;
    children?:ComponentChildren;
}

export const ToolWorkbench:FunctionComponent<ToolWorkbenchProps> = function ({
    result,
    inputsLabel = 'Inputs',
    resultsLabel = 'Results',
    emptyHint = 'Enter your values and select Calculate to see results.',
    children,
}) {
    const kids = toChildArray(children).filter(node =>
        typeof node !== 'string' || node.trim() !== '')
    const form = kids[0]
    const resultNode = result === undefined ? kids[1] : result

    return html`<div class="tool-workbench">
        <section class="tool-workbench-col">
            <p class="tool-workbench-label">${inputsLabel}</p>
            <div class="tool-workbench-card">${form}</div>
        </section>
        <section
            class="tool-workbench-col tool-workbench-results"
            aria-live="polite"
        >
            <p class="tool-workbench-label">${resultsLabel}</p>
            ${resultNode ||
                html`<div class="tool-workbench-empty">
                    <svg
                        class="tool-workbench-empty-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M4 18a8 8 0 0 1 16 0" />
                        <path d="M12 18l4 -6" />
                        <circle cx="12" cy="18" r="1" />
                    </svg>
                    <p class="tool-workbench-empty-title">
                        Results will appear here
                    </p>
                    <p class="tool-workbench-empty-hint">${emptyHint}</p>
                </div>`
            }
        </section>
    </div>`
}

export const Disclaimer:FunctionComponent = function () {
    return html`<aside class="tool-disclaimer" role="note">
        <p class="tool-disclaimer-label">For planning only</p>
        <p class="tool-disclaimer-text">
            This tool is for educational and operational planning
            purposes only. It is not legal advice and does not predict
            enforcement outcomes. Consult a qualified professional for
            regulatory compliance guidance.
        </p>
    </aside>`
}
